/**
 * The trick that was just won, held long enough to look at.
 *
 * The cards are swept into the winner's pile by the same move that completes
 * the trick, so the finished trick existed for no time at all - the table went
 * from three cards to none and the card that decided it was never seen. The
 * game now records it (`lastTrick`), and this is the two seconds in which
 * everybody reads it.
 *
 * Shown on the TABLE, because the whole room needs it: who won is the one fact
 * a trick produces, and hiding it on the winner's own phone tells the person
 * who already knows.
 */

import { useRef, useState } from 'react';
import { useCounterEvent } from '../render/use-counter-event.js';
import type { MatchView } from '../net/match.js';
import { Card } from '../render/Card.js';

interface Play {
  seat: number;
  card: string;
}

interface Won {
  plays: Play[];
  winner: number;
  n: number;
}

/** How long the finished trick stays up. Long enough to read, short enough not to nag. */
const HOLD_MS = 2000;

function readLastTrick(view: MatchView): Won | null {
  const raw = view.view['lastTrick'];
  if (typeof raw !== 'object' || raw === null) return null;

  const value = raw as { plays?: unknown; winner?: unknown; n?: unknown };
  if (!Array.isArray(value.plays)) return null;
  if (typeof value.winner !== 'number' || typeof value.n !== 'number') return null;

  const plays = value.plays.filter(
    (play): play is Play =>
      typeof play === 'object' &&
      play !== null &&
      typeof (play as Play).seat === 'number' &&
      typeof (play as Play).card === 'string',
  );
  if (plays.length === 0) return null;

  return { plays, winner: value.winner, n: value.n };
}

export function TrickWon({ view }: { view: MatchView }) {
  const [showing, setShowing] = useState<Won | null>(null);
  const timer = useRef<number | null>(null);

  const trick = readLastTrick(view);

  /*
   * Read through a ref, so the event below depends on the counter alone - see
   * `useCounterEvent` for why that matters. `readLastTrick` builds a fresh
   * object every render, and naming it in a dependency array is exactly what
   * left this banner on screen until the next trick replaced it.
   */
  const latest = useRef(trick);
  latest.current = trick;

  useCounterEvent(trick?.n ?? 0, () => {
    if (!latest.current) return;
    setShowing(latest.current);

    // A new trick replaces whatever was up, so its timer goes with it.
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setShowing(null), HOLD_MS);
  });

  if (!showing) return null;

  const winnerName = view.seats.find((seat) => seat.seat === showing.winner)?.displayName;

  return (
    <div className="trick-won" role="status">
      <div className="trick-won-cards">
        {showing.plays.map((play) => {
          const won = play.seat === showing.winner;
          const name = view.seats.find((seat) => seat.seat === play.seat)?.displayName;
          return (
            <div key={`${String(play.seat)}:${play.card}`} className={won ? 'trick-won-play won' : 'trick-won-play'}>
              {/* Above the card, so the eye lands on the label and then on what
                  won - rather than hunting for which card is bigger. */}
              {won ? <span className="trick-won-flag">Winner!</span> : null}
              <Card card={play.card} />
              <span className="trick-won-who">{name ?? `Seat ${String(play.seat + 1)}`}</span>
            </div>
          );
        })}
      </div>

      {/* Said in words as well as in size: a screen reader gets nothing from a
          card being 25% larger. */}
      <p className="trick-won-line">{winnerName ? `${winnerName} takes it` : 'Trick taken'}</p>
    </div>
  );
}
